import React, { useRef } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { useQueryClient } from 'react-query';
import { Controller, useForm } from 'react-hook-form';
import debounce from 'lodash/debounce';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Card } from '../../../../CommonComponents/OPL/Card/Card';
import { Slider } from '../../../../CommonComponents/OPL/Slider/Slider';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Label } from '../../../../CommonComponents/OPL/Label/Label';
import { HelperText } from '../../../../CommonComponents/OPL/HelperText/HelperText';
import { EstimatedPerformanceDTO } from '../../api/useGetWorkflowResultsQuery';
import { SplineChart } from './components/SplineChart/SplineChart';
import { SplineChartLoading } from './components/SplineChart/SplineChart.loading';
import { useWhatIfsMutation } from './api/useWhatIfsMutation';
import { useAutomatedAccountsQuery } from '../../../AutoAccountWorkflow/api/useAutomatedAccountsQuery';
import { parseMaskedInputString } from '../../../../CommonUtils/parseMaskedInputString';
import { queryKeys } from '../../../AutoAccountWorkflow/api/queryKeys';
import { useRouteParams } from '../../hooks/useRouteParams';

export const HOW_MUCH_SAVED = 'howMuchSaved';
export const TOTAL_INVESTABLE_ASSETS = 'totalInvestableAssets';
export const YEARS_BEFORE_WITHDRAWAL = 'yearsBeforeWithdrawal';

interface FormFields {
  howMuchSaved: number;
  totalInvestableAssets: number;
  yearsBeforeWithdrawal: number;
}

interface Props {
  series: EstimatedPerformanceDTO['series'];
  categories: string[];
}

const formatInput = (value, maxValue) => {
  if (value === '') {
    return 0;
  }
  if (parseMaskedInputString(value) > maxValue) {
    return maxValue;
  }
  return parseMaskedInputString(value);
};

export const WhatIfs: React.FC<Props> = ({ series, categories }) => {
  const { clientId } = useRouteParams();

  const { data } = useAutomatedAccountsQuery();

  const {
    mutateAsync: setWhatIfs,
    isLoading: isSettingWhatIfs,
    error: whatIfsError,
  } = useWhatIfsMutation();

  const { handleSubmit, setValue, control } = useForm<FormFields>();

  const queryClient = useQueryClient();

  const onSubmit = handleSubmit(async formData => {
    try {
      await setWhatIfs(formData);
    } catch (err) {
      console.error(err);
    }
  });

  const debouncedOnSubmit = useRef(debounce(onSubmit, 500)).current;

  useEffectOnce(() => () => {
    queryClient.removeQueries(queryKeys.automatedAccounts(clientId));
    debouncedOnSubmit.cancel();
  });

  const mappedCategories = categories.map((year, i) => {
    if (i === 0) {
      return 'Today';
    }
    return year;
  });

  if (!data) {
    return null;
  }

  const {
    whatIfOptions: {
      monthlyContribution,
      monthlyContributionMax,
      monthlyContributionMin,
      startingInvestmentMin,
      startingInvestment,
      startingInvestmentMax,
      yearsBeforeWithdrawal,
      yearsBeforeWithdrawalMax,
      yearsBeforeWithdrawalMin,
    },
  } = data;

  return (
    <Card mt="32" p="32" width="550px" id="what-ifs-card">
      {whatIfsError && <Alert message={whatIfsError.message} variant="error" mb="16" />}

      <Box height="300px">
        {isSettingWhatIfs ? (
          <SplineChartLoading series={series} categories={mappedCategories} />
        ) : (
          <SplineChart series={series} categories={mappedCategories} />
        )}
      </Box>

      <form onSubmit={e => e.preventDefault()}>
        <Controller
          control={control}
          name={TOTAL_INVESTABLE_ASSETS}
          defaultValue={startingInvestment}
          render={({ field: { value } }) => (
            <Box display="flex" flexDirection="column" mt="32">
              <Label htmlFor="input--starting-investment" mb="4">
                Starting Investment
              </Label>
              <Box display="flex">
                <Box width="50%" mr="8">
                  <InputNumberMask
                    leftIcon="fa fa-dollar-sign"
                    value={value}
                    disabled={isSettingWhatIfs}
                    onBlur={({ target }) => {
                      setValue(
                        TOTAL_INVESTABLE_ASSETS,
                        formatInput(target.value, startingInvestmentMax),
                      );
                      onSubmit();
                    }}
                    allowNegative={false}
                    id="starting-investment"
                  />
                </Box>
                <Box width="50%" m="auto" ml="8">
                  <Slider
                    ariaLabels={['Starting Investment Slider']}
                    values={[value]}
                    min={startingInvestmentMin}
                    max={startingInvestmentMax}
                    disabled={isSettingWhatIfs}
                    onChange={thumbValues => {
                      setValue(TOTAL_INVESTABLE_ASSETS, thumbValues[0]);
                      debouncedOnSubmit();
                    }}
                    showTooltip={false}
                  />
                </Box>
              </Box>
            </Box>
          )}
        />

        <Controller
          control={control}
          name={HOW_MUCH_SAVED}
          defaultValue={monthlyContribution}
          render={({ field: { value } }) => (
            <Box display="flex" flexDirection="column" mt="32">
              <Label htmlFor="input--monthly-contribution" mb="4">
                Monthly Contribution
              </Label>
              <Box display="flex">
                <Box width="50%" mr="8">
                  <InputNumberMask
                    leftIcon="fa fa-dollar-sign"
                    value={value}
                    disabled={isSettingWhatIfs}
                    onBlur={({ target }) => {
                      setValue(HOW_MUCH_SAVED, formatInput(target.value, monthlyContributionMax));
                      onSubmit();
                    }}
                    allowNegative={false}
                    id="monthly-contribution"
                  />
                </Box>
                <Box width="50%" m="auto" ml="8">
                  <Slider
                    ariaLabels={['Monthly Contribution Slider']}
                    values={[value]}
                    min={monthlyContributionMin}
                    max={monthlyContributionMax}
                    disabled={isSettingWhatIfs}
                    onChange={thumbValues => {
                      setValue(HOW_MUCH_SAVED, thumbValues[0]);
                      debouncedOnSubmit();
                    }}
                    showTooltip={false}
                  />
                </Box>
              </Box>
            </Box>
          )}
        />

        <Controller
          control={control}
          name={YEARS_BEFORE_WITHDRAWAL}
          defaultValue={yearsBeforeWithdrawal}
          render={({ field: { value } }) => (
            <Box display="flex" flexDirection="column" mt="32">
              <Label htmlFor="input--years-before-withdrawal" mb="4">
                Years Before Withdrawal
              </Label>
              <Box display="flex">
                <Box width="50%" mr="8">
                  <InputNumberMask
                    value={value}
                    disabled={isSettingWhatIfs}
                    onBlur={({ target }) => {
                      setValue(
                        YEARS_BEFORE_WITHDRAWAL,
                        formatInput(target.value, yearsBeforeWithdrawalMax),
                      );
                      onSubmit();
                    }}
                    allowNegative={false}
                    id="years-before-withdrawal"
                    aria-describedby="years-before-withdrawal-desc"
                  />
                </Box>
                <Box width="50%" m="auto" ml="8">
                  <Slider
                    ariaLabels={['Years Before Withdrawal Slider']}
                    values={[value]}
                    min={yearsBeforeWithdrawalMin}
                    max={yearsBeforeWithdrawalMax}
                    disabled={isSettingWhatIfs}
                    onChange={thumbValues => {
                      setValue(YEARS_BEFORE_WITHDRAWAL, thumbValues[0]);
                      debouncedOnSubmit();
                    }}
                    showTooltip={false}
                  />
                </Box>
              </Box>
              <Box width="50%">
                <HelperText mt="4" descriptionId="years-before-withdrawal-desc">
                  In how many years will you need to start withdrawing money from this account?
                </HelperText>
              </Box>
            </Box>
          )}
        />
      </form>
    </Card>
  );
};
